import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { formatISO, formatISO9075 } from 'date-fns'
import { format } from 'date-fns-tz'
import i18next from 'i18next'
import { isThisYear, isToday, newDate } from './index'

//TODO: base the currency / locale in the Account information instead
const NUM_LOCALE = 'en-US'
const NBSP       = '\u00A0'

const CURRENCY = new Intl.NumberFormat(NUM_LOCALE, { style: 'currency', currency: 'USD' })
const PERCENT  = new Intl.NumberFormat(NUM_LOCALE, { style: 'percent', maximumFractionDigits: 3 })

/**
 * Formats "integer-ed" currency values.
 * @example money(199) = $1.99
 * @param {int|string} integerValue
 * @returns {string}
 */
const money = (integerValue) => CURRENCY.format(integerValue / 100)

/**
 * Clears up a formatted money value from the backend, so it can be properly formatted as needed by us.
 * >**Note:** please, AVOID doing math with floats, this is dangerous and is exactly the reason why currency is stored as integers in the backend.
 * @param {string} stringValue
 * @returns {?number}
 */
const demoney = (stringValue) => {
	const cleanString = stringValue.replace(/[^\d]/g, '')
	return cleanString? parseInt(cleanString) : null
}

/**
 * Formats plain percents, or "integer-ed" values.
 * @example
 *  percent(5.99) //=> 5.99%
 *  percent(599, true) //=> 5.99%
 * @param value
 * @param fullInteger
 * @returns {string}
 */
const percent = (value, fullInteger = false) => PERCENT.format(value / (fullInteger? 10000 : 100))

/**
 * Our default format for flexible dates in the app. Hides the year when unnecessary, and allows for some flexibility
 * in the formatting, but not too much.
 * @param date {Date|string}
 * @param forceDate {boolean} Overrides the default behavior of skipping the date part if it's today
 * @param forceToday {boolean} Overrides the default behavior of skipping the date part if it's today, showing as "Today"
 * @param noTime {boolean} Shows just the date
 * @param onlyTime {boolean} Shows just the time
 * @param forceTZ {boolean} Includes the timezone when time is shown
 * @param noYear {boolean} Never shows year in the date part
 * @param month {string} The month format, according to {@link format}
 * @param day {string} The day format, according to {@link format}
 * @param is24 {boolean} Shows the time in 24-hour format instead of the default 12h (AM/PM)
 * @see https://date-fns.org/v2.16.1/docs/format date-fns.format() patterns
 * @returns {string}
 * @todo might need date-fns-tz to always output times in the Restaurant's timezone (if uses of formatDistance() don't get affected)
 */
const date = (date, { forceDate = false, forceToday = false, noTime = false, onlyTime = false, forceTZ = false, noYear = false, month = 'MMM', day = 'd', is24 = false } = {}) => {
	date = (typeof date == 'string')? newDate(null, date) : date
	let datePiece

	if (!onlyTime) {
		const yearPiece = noYear || isThisYear(date)? '' : `,'${NBSP}'yyyy`
		datePiece = forceToday && isToday(date)? `'Today'` :
			forceDate || noTime || !isToday(date)? `${month}'${NBSP}'${day}${yearPiece}` : ''
	} else {
		datePiece = ''
	}
	//TODO to keep in track with most common text style guides, I guess we should change to a lowercase, non-spaced AM/PM. If that happens, remember to change the replacer at Restaurant.js! https://knowadays.com/blog/writing-the-time-am-and-pm-or-a-m-and-p-m/
	const timePiece = noTime? '' : (is24? `HH:mm` : `h:mm'${NBSP}'a`)+(forceTZ? ' (z)' : '')
	const glue      = datePiece && timePiece? ' @ ' : ''
	return format(date, datePiece + glue + timePiece, { timeZone: window.DEFAULT_TIMEZONE })
}

/**
 * Same as {@link date()}, but forces time-only formatting
 * @param time {Date|string}
 * @param opt {object} Refer to {@link date()} for the complete list of options
 * @return {string}
 */
const time = (time, opt = {}) => date(time, Object.assign(opt, { onlyTime: true }))

/**
 * Turns a Date object into Y-M-D.
 * We could use formatISO(...,{rep:'date'}), but this is shorter :)
 * @param date {Date}
 * @return {string}
 */
const dateISO = date => format(date, 'yyyy-MM-dd')

/**
 * Turns a Date object into an ISO date+time string.
 * Basically an alias for {@link formatISO}, to keep all formatters we use at the same place.
 * @param date {Date}
 * @param complete {boolean} If true, spits out a formatted string including the timezone and that T in between.
 * @return {string}
 */
const ISO = (date, complete = false) => (complete? formatISO : formatISO9075)(date)

/**
 * Formats a date given the specific formatting string given, timezone-aware. Mostly used if you really need to show off the default timezone, otherwise the default {@link format()} suffices.
 * @param fmt
 * @param date
 */
const dateFlex = (fmt, date) => format(date, fmt, { timeZone: window.DEFAULT_TIMEZONE })

const period = (first, last, forceDay = false) => {
	const day = 'do'
	const forcedDay = forceDay? ` ${day}` : ''
	const forcedDOW = forceDay? 'EEEE, ' : ''
	const longMonth = forcedDay? 'MMM' : 'MMMM'
	if (first.getFullYear() != last.getFullYear()) {
		return `${format(first, `EEEE, MMM${forcedDay}, yyyy`)} ~ ${format(last, `EEEE, MMM${forcedDay}, yyyy`)}`
	}
	if (first.getMonth() != last.getMonth()) {
		return `${format(first, `${forcedDOW}MMM${forcedDay}`)} ~ ${format(last, `${forcedDOW}MMM${forcedDay}`)}`
	}
	if (first.getDate() != last.getDate()) {
		return `${format(first, `EEEE, ${longMonth} ${day}`)} ~ ${format(last, `EEEE, ${day}`)}`
	}
	return format(first, `EEEE, ${longMonth} ${day}`)
}

/**
 * @typedef {object} FMT.orderType
 * @property {string} name
 * @property {function(forceBase:boolean=false):string} getName
 * @property {function(attr:object):JSX.Element} Icon
 */
/**
 * Helpful UI elements to format order types
 * @param base {string}
 * @param displayName {?string}
 * @returns {FMT.orderType}
 */
const orderType = (base, displayName = null) => {
	displayName = displayName || base

	return {
		//why two name getters, you ask? `getName` is an arrow function, and thus creates no context, so `this` becomes the actual order; then, the usual getter `name` uses this behavior to borrow order details while keeping its own context, as mandated by JS syntax.
		get name() { return this.getName() },

		getName: (forceBase = false) => {
			switch ((forceBase? base : displayName).toUpperCase()) {
				case 'PICK UP' :
				case 'PICK-UP' :
				case 'PICKUP'  : return i18next.t('Pick up')
				case 'DINE-IN' :
				case 'DINEIN'  : return i18next.t('Dine-in')
				case 'DELIVERY': return i18next.t('Delivery')
				default        : return displayName
			}
		},

		Icon: attr => {
			let icon
			//uses the type display name, so we can ignore any custom type (unless it has the same name lol)
			//lowerCase, so it's clear we're NOT dealing with the base type field
			switch (displayName.toLowerCase()) {
				case 'pick up' :
				case 'pick-up' :
				case 'pickup'  : icon = 'walking';        break
				case 'dine-in' :
				case 'dinein'  : icon = 'plate-utensils'; break
				case 'delivery': icon = 'car-side';       break
				default        : icon = 'shopping-bag'; //TODO: no icons for custom types yet - see sc-47454
			}
			return <Icon icon={icon} {...attr} />
		},
	}
}

/*
/**
 * Overly simplified, but kinda personalized, version of what PHP's striptags() would do?
 * @see https://css-tricks.com/snippets/javascript/strip-html-tags-in-javascript/
 * @param str
 *//*
const stripTags = str => {
	return str.replace(/(<([^>]+)>)/gi, '')
}
*/

const FMT = {
	ISO, date, dateFlex, dateISO, demoney, money, orderType, percent, period, time,
}

export default FMT
